/**
 * A textarea that grows to fit what is in it.
 *
 * Notes started at two rows, which is fine for "ring the dentist" and useless for a note
 * that runs to twenty lines — and a box showing two lines of twenty invites you to write
 * somewhere else instead, which is the one thing §0 is trying to prevent.
 *
 * So it starts where it was and grows. `rows` still sets the floor, so an empty box is the
 * same small box it always was.
 */

import { useLayoutEffect, useRef } from "react";

/**
 * The ceiling lives in CSS, as `max-height: min(40rem, 70svh)` on `.input.notes`.
 *
 * Two limits, whichever bites first, because they guard different things. 40rem is a
 * measure — about 26 lines — and it scales with the reader's text size, so a large font
 * still gets 26 lines rather than fewer. 70svh is a fence: whatever the text size, the box
 * must not be taller than the screen it is on.
 *
 * `svh` rather than `vh` or `dvh`. `vh` is the *large* viewport on mobile — the height with
 * the browser chrome hidden — so it promises room that is not there while the chrome is
 * showing. `dvh` tracks the chrome as it collapses, which means the box would resize under
 * your thumb as you scroll. `svh` is the small viewport: never more room than actually
 * exists, and it does not move.
 *
 * Doing it in CSS rather than here also means it re-evaluates on rotation without this hook
 * running again.
 */

function rootFontSize(): number {
  const size = Number.parseFloat(window.getComputedStyle(document.documentElement).fontSize);
  return Number.isFinite(size) && size > 0 ? size : 16;
}

export function useAutoGrow(value: string) {
  const ref = useRef<HTMLTextAreaElement>(null);

  // Layout, not effect: measuring and resizing after paint means one frame at the wrong
  // height, which on an opening editor is a visible jump.
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Collapse first. `scrollHeight` reports the content height *or* the current height,
    // whichever is larger, so without this the box can only ever grow — delete half a note
    // and it keeps the space.
    element.style.height = "auto";

    // In rem rather than px, so a height set now survives the reader changing their text
    // size later — the box scales with the words instead of cropping them. `max-height` in
    // the stylesheet clamps it; `scrollHeight` reports the full content either way.
    element.style.height = `${element.scrollHeight / rootFontSize()}rem`;
  }, [value]);

  return ref;
}
